import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useCompanyName } from "@/lib/company";
import { Page, PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import SendDocument from "@/components/SendDocument";
import { cn } from "@/lib/utils";
import {
  FORMAT_LABEL, allowed, docName, periodLabel, preparedLabel, keptLabel, previewTitle, previewLine, checkLine,
  sourceRows, mismatchLine, problemLine, credits, heldLine, dropLine,
} from "@/lib/documents";
import { DocStatus, useFiles } from "@/pages/owner/Documents";
import {
  ArrowLeft, Sun, Moon, Monitor, Smartphone, ShieldCheck, TriangleAlert, LoaderCircle, Trash2, CirclePause, Maximize2, Minimize2,
} from "lucide-react";

// design_handoff_founder_assistant/screens/Preview.dc.html (screen 4): one document as its reader will see it, the
// email in light or dark mode (email apps change colours) on a desktop or a phone, or the PDF; beside it what can be
// done with it, the number check with its sources, and its facts. A document whose numbers didn't match is shown with
// what doesn't match and can't be downloaded or copied. Send and Send again open the send dialog
// (components/SendDocument.jsx); a held update's Send now and Skip come with part 4b. On a phone the side panel goes under
// the preview.
const PANEL = "bg-card rounded-[10px] p-4 flex flex-col gap-3";

// "About $0.40 of credits."
const about = (usd) => { const line = usd ? credits(usd) : ""; return line && `${line[0].toUpperCase()}${line.slice(1)}.`; };

// the email as an email app shows it: our own HTML in a frame that runs nothing; its dark look forced by the
// template's class, never by the app's own colour scheme
// `hidden`: blocks left out (the scheduler's live preview hides the sections it won't send)
export function emailPage(html, dark, hidden = []) {
  const hide = hidden.length ? `<style>${hidden.map((b) => `[data-block="${b}"]`).join(",")}{display:none !important}</style>` : "";
  const page = html.replace("@media (prefers-color-scheme:dark)", "@media not all").replace("</head>", `${hide}</head>`);
  return dark ? page.replace('<body class="cb-page"', '<body class="cb-page cb-dark"') : page;
}

function Toggle({ label, value, onChange, options }) {
  return (
    <span role="group" aria-label={label} className="inline-flex rounded-md border border-gr-control-border overflow-hidden">
      {options.map(([v, text, Icon]) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)} data-testid={`preview-${v}`}
          className={cn("h-[30px] px-2.5 inline-flex items-center gap-1.5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
            value === v ? "bg-gr-selected text-gr-on-selected font-semibold" : "hover:bg-gr-hover")}>
          {Icon && <Icon className="w-3.5 h-3.5" aria-hidden="true" />}{text}
        </button>
      ))}
    </span>
  );
}

export function EmailFrame({ html, dark, narrow, hidden }) {
  const frame = useRef(null);
  const [height, setHeight] = useState(800);
  const fit = () => {
    const d = frame.current?.contentDocument;
    if (d?.documentElement) setHeight(d.documentElement.scrollHeight);
  };
  return (
    <iframe ref={frame} title="The email" sandbox="allow-same-origin" srcDoc={emailPage(html, dark, hidden)} onLoad={fit}
      className="block mx-auto border-0 max-w-full shadow-[0_0_0_1px_var(--gr-hairline)]"
      style={{ width: narrow ? 375 : 680, height }} data-testid="preview-email" />
  );
}

// Full screen: the preview and its switches fill the window at once, and the screen too where the browser allows it
// (not an iPhone, nor a browser that never answers); Escape, leaving the browser's full screen or the button brings them
// back
function useFullScreen() {
  const box = useRef(null);
  const [full, setFull] = useState(false);
  useEffect(() => {
    if (!full) return undefined;
    const left = () => !document.fullscreenElement && setFull(false);
    const onKey = (e) => e.key === "Escape" && setFull(false);
    document.addEventListener("fullscreenchange", left);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("fullscreenchange", left); window.removeEventListener("keydown", onKey); };
  }, [full]);
  useEffect(() => () => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); }, []);
  const toggle = () => {
    if (full) {
      setFull(false);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    } else {
      setFull(true);
      box.current?.requestFullscreen?.().catch(() => {});
    }
  };
  return { box, full, toggle };
}

function Middle({ icon: Icon, tone, title, children, spin }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-2.5 text-center p-6">
      <Icon className={cn("w-7 h-7", tone, spin && "motion-safe:animate-spin")} aria-hidden="true" />
      <span className={cn("font-display text-[18px] leading-6 font-semibold", tone)}>{title}</span>
      {children && <span className="max-w-[420px] text-muted-foreground">{children}</span>}
    </div>
  );
}

function NumberCheck({ check }) {
  const ok = check.matching === check.count;
  return (
    <section aria-label="Number check" className={PANEL} data-testid="number-check">
      <span className="flex gap-2">
        <ShieldCheck className={cn("w-4 h-4 shrink-0 mt-0.5", ok ? "text-gr-success" : "text-gr-danger")} aria-hidden="true" />
        <span className="font-semibold">{checkLine(check)}</span>
      </span>
      <details open className="border-t pt-2">
        <summary className="cursor-pointer font-semibold min-h-7 flex items-center">Sources</summary>
        <ul className="m-0 mt-1.5 p-0 list-none flex flex-col">
          {sourceRows(check).map((s, i) => (
            <li key={`${s.key}-${i}`} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 py-2 border-t" data-testid="number-source">
              <Link to={s.link} className="font-semibold truncate hover:underline">{s.name}</Link>
              <span className={cn("text-end tabular-nums", s.bad && "text-gr-danger")}>{s.count}</span>
              <span className={cn("text-xs uppercase tracking-[0.09em]", s.bad ? "text-gr-danger" : "text-muted-foreground")}>{s.kind}</span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

export default function DocumentPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const company = useCompanyName();
  const files = useFiles(company);
  const { data, isLoading, error } = useQuery({
    queryKey: ["document", id], queryFn: async () => (await api.get(`/documents/${id}`)).data.document,
    refetchInterval: (q) => (q.state.data?.status === "preparing" ? 5000 : false),
  });
  const doc = data;
  const shows = doc && ["ready", "nocheck", "sent"].includes(doc.status);
  const [view, setView] = useState(null);
  const [mode, setMode] = useState("light");
  const [narrow, setNarrow] = useState(false);
  const fs = useFullScreen();
  const shown = view || (doc?.files?.email ? "email" : "pdf");
  const email = useQuery({
    queryKey: ["document-email", id, doc?.prepared_at], enabled: !!(shows && doc?.files?.email),
    queryFn: async () => (await api.get(`/documents/${id}/files/email`, { params: { view: true }, responseType: "text" })).data,
  });
  const pdf = useQuery({
    queryKey: ["document-pdf", id, doc?.prepared_at], enabled: !!(shows && doc?.files?.pdf && shown === "pdf"),
    queryFn: async () => URL.createObjectURL((await api.get(`/documents/${id}/files/pdf`, { params: { view: true }, responseType: "blob" })).data),
  });
  useEffect(() => () => pdf.data && URL.revokeObjectURL(pdf.data), [pdf.data]);
  // a held update (no credits when it was due): Send now prepares it again and sends it, Skip drops it
  const held = useMutation({
    mutationFn: async (how) => (await api.post(`/documents/${id}/${how}`)).data,
    onSuccess: (_d, how) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      if (how === "skip") { toast.success("Skipped. Nothing was sent."); navigate("/founder/documents"); }
      else { qc.invalidateQueries({ queryKey: ["document", id] }); toast.success("Being prepared. It goes out as soon as it's ready."); }
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const retry = useMutation({
    mutationFn: async () => (await api.post(`/documents/${id}/retry`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["document", id] }); qc.invalidateQueries({ queryKey: ["documents"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const [deleting, setDeleting] = useState(false);
  const [sending, setSending] = useState(false);
  const remove = useMutation({
    mutationFn: async (password) => api.delete(`/documents/${id}`, { data: { password } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["documents"] }); toast.success("Document deleted."); navigate("/founder/documents"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const back = (
    <Link to="/founder/documents" className="self-start inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="w-3.5 h-3.5 rtl:-scale-x-100" aria-hidden="true" />Documents
    </Link>
  );
  const eyebrow = [company, "Documents", "Preview"].filter(Boolean).join(" · ");
  if (isLoading) return <Page className="gap-5">{back}<Loading /></Page>;
  if (error || !doc) return <Page className="gap-5">{back}<PageHeader eyebrow={eyebrow} title="No such document." /></Page>;

  const can = allowed(doc);
  const kept = keptLabel(doc);
  const tryAgain = (
    <>
      <Button className="self-start" onClick={() => retry.mutate()} disabled={retry.isPending} data-testid="preview-retry">Try again</Button>
      <span className="text-sm text-muted-foreground">Trying again prepares the numbers again{doc.estimate_usd ? `: ${credits(doc.estimate_usd)}` : ""}.</span>
    </>
  );
  const keep = (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" className="flex-1" disabled={!can.pdf || files.download.isPending} onClick={() => files.download.mutate(doc)}
        data-testid="preview-download">Download PDF</Button>
      <Button variant="outline" className="flex-1" disabled={!can.copy || files.copy.isPending} onClick={() => files.copy.mutate(doc)}
        data-testid="preview-copy">Copy email</Button>
    </div>
  );
  return (
    <Page className="gap-5" data-testid="document-preview">
      {back}
      <PageHeader eyebrow={eyebrow} title={previewTitle(doc)} status={previewLine(doc, doc.credits_ok)} testid="preview-header" />
      <div className="flex flex-wrap items-start gap-6">
        <section ref={fs.box} aria-label="Preview" className={cn("flex-[999_1_560px] min-w-0 flex flex-col gap-3",
          fs.full && "fixed inset-0 z-50 bg-background p-4")} data-testid="preview-box">
          {shows && (
            <div className="flex flex-wrap items-center gap-3">
              {doc.files?.email && (
                <Toggle label="Show" value={shown} onChange={setView} options={[["email", "Email"], ["pdf", "PDF"]]} />
              )}
              {shown === "email" && (<>
                <Toggle label="Email app colours" value={mode} onChange={setMode} options={[["light", "Light", Sun], ["dark", "Dark", Moon]]} />
                <Toggle label="Width" value={narrow ? "phone" : "desktop"} onChange={(v) => setNarrow(v === "phone")}
                  options={[["desktop", "Desktop", Monitor], ["phone", "Phone", Smartphone]]} />
              </>)}
              {doc.subject && shown === "email" && <span className="ms-auto text-sm text-muted-foreground">Subject: {doc.subject}</span>}
              <Button variant="ghost" className={cn("px-2.5", !(doc.subject && shown === "email") && "ms-auto")} onClick={fs.toggle}
                data-testid="preview-full-screen">
                {fs.full ? <><Minimize2 />Exit full screen</> : <><Maximize2 />Full screen</>}
              </Button>
            </div>
          )}
          <div className={cn("overflow-auto rounded-[10px] bg-card p-6 box-border", fs.full ? "flex-1 min-h-0" : "h-[820px]")}>
            {doc.status === "preparing" ? (
              <Middle icon={LoaderCircle} tone="text-gr-ink-secondary" title="Preparing" spin />
            ) : doc.status === "held" ? (
              <Middle icon={CirclePause} tone="text-gr-warning" title="Held: no credits">
                The numbers are prepared again when you send it, so the preview shows up then. Nothing has been sent.
              </Middle>
            ) : doc.status === "failed" ? (
              <Middle icon={TriangleAlert} tone="text-gr-danger" title="Couldn't prepare it">{problemLine(doc.problem)}</Middle>
            ) : !shows ? null : shown === "email" ? (
              email.data ? <EmailFrame html={email.data} dark={mode === "dark"} narrow={narrow} /> : <Loading />
            ) : pdf.data ? (
              <iframe title="The PDF" src={pdf.data} className="block w-full h-full border-0 rounded-md bg-white" data-testid="preview-pdf" />
            ) : <Loading />}
          </div>
        </section>

        <aside aria-label="About this document" className="flex-[1_1_300px] max-w-[360px] min-w-0 flex flex-col gap-3">
          {["ready", "sent"].includes(doc.status) && (
            <section className={PANEL} data-testid="preview-ready">
              <DocStatus doc={doc} />
              <Button className="w-full" onClick={() => setSending(true)} data-testid="preview-send">
                {doc.status === "sent" ? "Send again" : "Send"}
              </Button>
              {keep}
            </section>
          )}
          {doc.status === "held" && (
            <section className="border border-gr-warning rounded-[10px] p-4 flex flex-col gap-2.5" data-testid="preview-held">
              <DocStatus doc={doc} />
              <p className="m-0 [text-wrap:pretty]">{heldLine(doc, doc.credits_ok)}</p>
              <div className="flex flex-wrap gap-2">
                {!doc.credits_ok && <Button asChild><Link to="/founder/store#topup">Add credits</Link></Button>}
                <Button variant={doc.credits_ok ? "default" : "outline"} disabled={!doc.credits_ok || held.isPending}
                  onClick={() => held.mutate("send-now")} data-testid="preview-send-now">Send now</Button>
                <Button variant="ghost" disabled={held.isPending} onClick={() => held.mutate("skip")} data-testid="preview-skip">Skip</Button>
              </div>
              <span className="text-sm text-muted-foreground">
                {doc.credits_ok ? about(doc.estimate_usd) : dropLine(doc)}
              </span>
            </section>
          )}
          {doc.status === "nocheck" && (
            <section role="alert" className="border border-gr-danger rounded-[10px] p-4 flex flex-col gap-2.5" data-testid="preview-nocheck">
              <span className="inline-flex items-center gap-1.5 font-semibold text-gr-danger">
                <TriangleAlert className="w-3.5 h-3.5" aria-hidden="true" />Couldn't check the numbers
              </span>
              <span>{mismatchLine(doc.number_check)}</span>
              {tryAgain}
              {keep}
            </section>
          )}
          {doc.status === "failed" && (
            <section role="alert" className="border border-gr-danger rounded-[10px] p-4 flex flex-col gap-2.5" data-testid="preview-failed">
              <span className="inline-flex items-center gap-1.5 font-semibold text-gr-danger">
                <TriangleAlert className="w-3.5 h-3.5" aria-hidden="true" />Couldn't prepare it
              </span>
              <span>{problemLine(doc.problem)}</span>
              {tryAgain}
            </section>
          )}
          {doc.number_check && ["ready", "nocheck", "sent"].includes(doc.status) && <NumberCheck check={doc.number_check} />}
          <section className={cn(PANEL, "gap-0.5 text-sm")}>
            {[["To", (doc.recipients || []).map((r) => r.name || r.email).filter(Boolean).join(", ") || "—"],
              ["Format", FORMAT_LABEL[doc.format]], ["Prepared", preparedLabel(doc)], ["Kept", kept.text]].map(([k, v]) => (
              <div key={k} className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 py-1">
                <span className="text-muted-foreground">{k}</span><span className={cn(k === "Kept" && kept.warn && "text-gr-warning")}>{v}</span>
              </div>
            ))}
            <Button variant="danger" className="mt-2 self-start px-2.5" onClick={() => setDeleting(true)} data-testid="preview-delete">
              <Trash2 />Delete
            </Button>
          </section>
        </aside>
      </div>
      <SendDocument doc={doc} open={sending} onOpenChange={setSending} onDocuments />
      <ConfirmDestructive open={deleting} onOpenChange={setDeleting} title="Delete this document?" action="Delete"
        pending={remove.isPending} onConfirm={(password) => remove.mutate(password)}>
        <p className="m-0 [text-wrap:pretty]">{docName(doc)}, {periodLabel(doc.period)}, and its files are deleted for good. Whoever
          it was sent to keeps their copy.</p>
      </ConfirmDestructive>
    </Page>
  );
}
